/**
 * Knight Arena Auth API — connects to the custom Node.js backend.
 * Base URL is controlled by VITE_AUTH_SERVER_URL (defaults to localhost:3001).
 */

const BASE_URL = (import.meta.env.VITE_AUTH_SERVER_URL || 'http://localhost:3001') + '/api/auth';
const TOKEN_KEY = 'ka_token';

// ---------------------------------------------------------------------------
// Token helpers
// ---------------------------------------------------------------------------

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

function setToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

// ---------------------------------------------------------------------------
// Fetch wrapper
// ---------------------------------------------------------------------------

async function request(path, { method = 'GET', body } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  let data;
  try {
    data = await res.json();
  } catch {
    data = {};
  }

  if (!res.ok) {
    const err = new Error(data?.error || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }

  return data;
}

// ---------------------------------------------------------------------------
// Auth API
// ---------------------------------------------------------------------------

/**
 * Register a new account.
 * @param {{ username: string, email: string, password: string }} payload
 * @returns {{ token: string, user: object }}
 */
export async function register(payload) {
  const data = await request('/register', { method: 'POST', body: payload });
  setToken(data.token);
  return data;
}

/**
 * Log in with email (or username) + password.
 * @param {{ email: string, password: string }} payload
 * @returns {{ token: string, user: object }}
 */
export async function login(payload) {
  const data = await request('/login', { method: 'POST', body: payload });
  setToken(data.token);
  return data;
}

/**
 * Fetch the currently authenticated user using the stored JWT.
 * @returns {{ user: object }}
 */
export async function me() {
  return request('/me');
}

/**
 * Change password for the currently authenticated user.
 * @param {{ currentPassword: string, newPassword: string }} payload
 */
export async function changePassword(payload) {
  return request('/change-password', { method: 'POST', body: payload });
}

/**
 * Clear local token and call the server logout endpoint.
 */
export async function logout() {
  try {
    await request('/logout', { method: 'POST' });
  } finally {
    clearToken();
  }
}

/**
 * Returns true if a JWT is present in localStorage.
 * Does NOT validate the token — call me() for that.
 */
export function isAuthenticated() {
  return Boolean(getToken());
}
